import React, { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import styled from "styled-components/native"

import AsyncView, { AsyncViewProps } from "../../../components/async-view"
import { AsyncViewOfflineProvider } from "../../../components/async-view-offline-provider"
import Box from "../../../components/box"
import CenteredContent from "../../../components/centered-content"
import { Heading } from "../../../components/heading"
import Row from "../../../components/row"
import { useRoute } from "../../../hooks/use-route"
import useSafeAreaInsetAppHeader from "../../../hooks/use-safe-area-inset-app-header"
import { Spacing } from "../../../lib/spacing"
import { i18nKey } from "../../../types/i18next"
import { MENU_TAB, getPathFromTab } from "../../routes"
import PageLayout from "../page"
import { MenuBackButton } from "./back-button"
import { MENU_CONTENT_MAX_WIDTH_PX } from "./utils"

const ContentContainer = styled(CenteredContent)<{ $headerHeight: number }>`
  margin-top: ${(props) => props.$headerHeight}px;
`

function MenuLayoutDesktop({
  children,
  asyncViewProps,
  backgroundColor = "tertiary",
  paddingHorizontal = "jumbo",
}: {
  children?: ReactNode
  asyncViewProps?: AsyncViewProps & { offlineStateDisabled?: boolean }
  backgroundColor?: string
  paddingHorizontal?: Spacing
}): React.JSX.Element {
  const headerHeight = useSafeAreaInsetAppHeader()
  const route = useRoute()
  const { t } = useTranslation()
  const title = route.title ? t(route.title as i18nKey) : undefined
  return (
    <PageLayout backgroundColor={backgroundColor}>
      <ContentContainer
        maxWidth={MENU_CONTENT_MAX_WIDTH_PX}
        padding="jumbo"
        $headerHeight={headerHeight}
      >
        <Box
          backgroundColor="primary"
          paddingTop={title ? "xlarge" : "jumbo"}
          paddingBottom="jumbo"
          paddingHorizontal={paddingHorizontal}
          borderRadius="roundedLarge"
        >
          {title ? (
            <Row
              paddingBottom="xlarge"
              alignItems="center"
              justifyContent="center"
              gap="medium"
            >
              {route.path !== getPathFromTab(MENU_TAB) ? (
                <StyledMenuBackButton />
              ) : null}
              <Heading level={2} textStyle="headingLarge">
                {title}
              </Heading>
            </Row>
          ) : null}
          {asyncViewProps ? (
            <AsyncViewOfflineProvider {...asyncViewProps}>
              <AsyncView>{children}</AsyncView>
            </AsyncViewOfflineProvider>
          ) : (
            children
          )}
        </Box>
      </ContentContainer>
    </PageLayout>
  )
}

const StyledMenuBackButton = styled(MenuBackButton)`
  position: absolute;
  left: 0;
`

export default MenuLayoutDesktop
